import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setSubmitState('success');
      return;
    }

    formData.delete('website_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      await fetch('https://readdy.ai/api/form/d918pe04jh3v1cgksstg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitState('success');
      setEmail('');
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <footer className="bg-accent-900 text-background-50">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, oklch(1 0 0) 1px, transparent 1px),
            radial-gradient(circle at 50% 60%, oklch(1 0 0) 1px, transparent 1px),
            radial-gradient(circle at 80% 20%, oklch(1 0 0) 1px, transparent 1px),
            radial-gradient(circle at 30% 80%, oklch(1 0 0) 1px, transparent 1px),
            radial-gradient(circle at 70% 70%, oklch(1 0 0) 1px, transparent 1px)`,
            backgroundSize: '200px 200px, 300px 300px, 250px 250px, 180px 180px, 220px 220px',
            backgroundPosition: '0 0, 50px 50px, 100px 100px, 150px 150px, 200px 200px',
          }}
        ></div>

        <div className="relative px-6 md:px-10 py-16">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              <div className="lg:col-span-2">
                <Link to="/" className="inline-flex items-center gap-2 mb-6">
                  <span className="font-heading text-xl font-semibold text-background-50">PandabuySpreadsheet</span>
                  <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                </Link>
                <p className="text-background-50/70 text-sm leading-relaxed mb-8 max-w-sm">
                  The smart way to compare products before you buy. Structured spreadsheets, data-driven comparisons, and real-world cost analysis for smarter shopping decisions.
                </p>
                <div className="max-w-sm">
                  <form
                    onSubmit={handleNewsletterSubmit}
                    className="flex w-full relative"
                    data-readdy-form=""
                  >
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email for updates"
                      required
                      className="flex-1 bg-transparent border border-background-50/20 rounded-l-lg px-4 py-2.5 text-sm text-background-50 placeholder:text-background-50/40 outline-none focus:border-background-50/40 transition-colors"
                    />
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden={true} />
                    <button
                      type="submit"
                      className="bg-background-50/15 border border-background-50/20 border-l-0 rounded-r-lg px-4 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-mail-send-line text-background-50"></i>
                    </button>
                  </form>
                  {submitState === 'success' && (
                    <p className="text-accent-300 text-xs mt-2">Thanks for subscribing!</p>
                  )}
                  {submitState === 'error' && (
                    <p className="text-red-400 text-xs mt-2">Something went wrong. Please try again.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm text-background-50 mb-4">Quick Links</h4>
                <ul className="space-y-2.5">
                  <li><Link to="/" className="text-background-50/60 text-sm hover:text-background-50/90 transition-colors">Home</Link></li>
                  <li><Link to="/blog/pandabuy-sheet-ultimate-guide" className="text-background-50/60 text-sm hover:text-background-50/90 transition-colors">Ultimate Guide</Link></li>
                  <li><a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-50/60 text-sm hover:text-background-50/90 transition-colors">Main Store</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-sm text-background-50 mb-4">Categories</h4>
                <ul className="space-y-2.5">
                  {categories.slice(0, 6).map((cat) => (
                    <li key={cat.imageUrl}>
                      <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-background-50/60 text-sm hover:text-background-50/90 transition-colors">{cat.name}</a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-sm text-background-50 mb-4">More Categories</h4>
                <ul className="space-y-2.5">
                  {categories.slice(6).map((cat) => (
                    <li key={cat.imageUrl}>
                      <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-background-50/60 text-sm hover:text-background-50/90 transition-colors">{cat.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-background-50/10">
        <div className="px-6 md:px-10 py-5">
          <div className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-background-50/50 text-xs">
              &copy; {new Date().getFullYear()} PandabuySpreadsheet. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded border border-background-50/20 text-background-50/60 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-global-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded border border-background-50/20 text-background-50/60 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-shopping-cart-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded border border-background-50/20 text-background-50/60 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-star-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded border border-background-50/20 text-background-50/60 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-heart-line text-sm"></i>
              </a>
            </div>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-50/50 text-xs hover:text-background-50/80 transition-colors">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}